import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LOCAL_STORAGE_AUTH_STATE, ROLES } from 'src/constants/auth';

import {
  getFromStorage,
  logoutFromUserFn,
  PayloadProps,
  removeLoginInformation,
  removeUserLoginInformation,
  setAuthStateBasedOnRole,
  setLoginInformation,
  setUserTokenFn,
} from './methods';

export const initialState: IAuthState = {
  currentUserToken: '',
  currentUser: undefined,
  currentAccount: undefined,
  superAdminToken: '',
  superAdminUser: undefined,
  fetching: false,
  accountsList: [],
  usersList: [],
  metadataRequested: false,
  accountsSearchValue: '',
  role: ROLES.PROMOTER,
  language: 'en-US',
};

// Modern RTK 2.10.1 async thunk with better error handling
export const loginAsUserAsync = createAsyncThunk(
  'auth/loginAsUser',
  async (payload: PayloadProps, { rejectWithValue }) => {
    try {
      // Your login logic here
      const setUserTokenState = setUserTokenFn(initialState, payload);
      setLoginInformation(setUserTokenState);
      return setUserTokenState;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const loginAsUserFn = (state: IAuthState, payload: PayloadProps) => {
  const setUserTokenState: IAuthState = setUserTokenFn(state, payload);
  setLoginInformation(setUserTokenState);
  return setUserTokenState;
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: getFromStorage(LOCAL_STORAGE_AUTH_STATE),
  reducers: {
    logoutRequest() {
      removeLoginInformation();
      return initialState;
    },
    logoutFromUser(state) {
      removeUserLoginInformation(state);
      return logoutFromUserFn(state);
    },
    loginAsUser(state, { payload }) {
      return loginAsUserFn(state, payload);
    },
    loginCompleted(state, { payload }) {
      const loginSuccessState: IAuthState = setAuthStateBasedOnRole(state, payload);
      setLoginInformation(loginSuccessState);
      return loginSuccessState;
    },
    metadataCompleted(state, { payload }) {
      const newState = {
        ...state,
        currentAccount: {
          ...payload,
        },
        metadataRequested: true,
      };
      setLoginInformation(newState);
      return newState;
    },
    logoutCompleted() {
      removeLoginInformation();
      return initialState;
    },
    accountUsersCompleted(state, { payload }) {
      return { ...state, usersList: payload };
    },
    updateUserField(state, { payload }) {
      const updatedState = {
        ...state,
        currentUser: {
          ...state.currentUser,
          [payload[0]]: payload[1],
        },
      };
      setLoginInformation(updatedState);
      return updatedState;
    },
    updateCurrentAccountField(state, { payload }) {
      const updatedState = {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          [payload[0]]: payload[1],
        },
      };
      setLoginInformation(updatedState);
      return updatedState;
    },
    updateSuperAdminField(state, { payload }) {
      const updatedState = {
        ...state,
        superAdminUser: {
          ...state.superAdminUser,
          [payload[0]]: payload[1],
        },
      };
      setLoginInformation(updatedState);
      return updatedState;
    },
    updateAccounts(state, { payload }) {
      if (payload?.accounts) {
        const updatedState = {
          ...state,
          currentUser: {
            ...state.currentUser,
            accounts: {
              data: payload.accounts,
            },
          },
        };
        setLoginInformation(updatedState);
        return updatedState;
      }
      return state;
    },
    removeCurrentAccountAndUser(state) {
      return { ...state, currentAccount: undefined, currentUser: undefined };
    },
    removeUsersList(state) {
      return { ...state, usersList: [] };
    },
    updateAccountsSearchValue(state, { payload }) {
      return { ...state, accountsSearchValue: payload };
    },
    updateAccountVehiclesCount(state, { payload }) {
      const newState = {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          vehicles_count: state.currentAccount.vehicles_count + payload,
        },
      };
      setLoginInformation(newState);
      return newState;
    },
    updateAccountSettings(state, { payload }) {
      const newState = {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          settings: payload,
        },
      };
      setLoginInformation(newState);
      return newState;
    },
    updateAccountUsersCount(state, { payload }) {
      const newState = {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          users_count: state.currentAccount.users_count + payload,
        },
      };
      setLoginInformation(newState);
      return newState;
    },
    updateAccountSubscriptionPlan(state, { payload }) {
      return {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          subscription_plan: payload,
        },
      };
    },
    updateAccountImporters(state, { payload }) {
      return {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          importers: payload,
        },
      };
    },
    updateAccountAdmin(state, { payload }) {
      return {
        ...state,
        currentAccount: {
          ...state.currentAccount,
          admin: payload,
        },
      };
    },
  },
  // Modern RTK 2.10.1 extraReducers for async thunks
  extraReducers: (builder) => {
    builder
      .addCase(loginAsUserAsync.pending, (state) => {
        state.fetching = true;
      })
      .addCase(loginAsUserAsync.fulfilled, (state, action: PayloadAction<IAuthState>) => {
        state.fetching = false;
        return action.payload;
      })
      .addCase(loginAsUserAsync.rejected, (state) => {
        state.fetching = false;
      });
  },
});

export const {
  logoutFromUser,
  loginAsUser,
  loginCompleted,
  metadataCompleted,
  logoutCompleted,
  accountUsersCompleted,
  updateUserField,
  updateSuperAdminField,
  updateAccounts,
  removeCurrentAccountAndUser,
  removeUsersList,
  updateAccountsSearchValue,
  updateAccountVehiclesCount,
  updateAccountUsersCount,
  updateAccountSubscriptionPlan,
  updateAccountSettings,
  updateAccountImporters,
  updateAccountAdmin,
  updateCurrentAccountField,
} = authSlice.actions;

export const getAuth = (state: IReducerStates): IAuthState => state.auth;
